import { resetPosthogSession } from './analytics.js';

const API_BASE = '/api';
const TOKEN_KEY = 'jwt_token';
const API_KEY_KEY = 'api_key';
// Holds the admin's own token while impersonating, so "Exit" can restore it.
const IMPERSONATION_BACKUP_KEY = 'jwt_token_admin_backup';

export class AuthError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

const MAINT_BYPASS_KEY = 'cx_maint_bypass';

// Bootstrap: read apiKey / maintenance-bypass token from the URL and store.
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  const key = params.get('apiKey');
  if (key) localStorage.setItem(API_KEY_KEY, key);
  const bypass = params.get('bypass');
  if (bypass) localStorage.setItem(MAINT_BYPASS_KEY, bypass);
}

// When present, sent as x-maintenance-bypass on every request so an admin can
// reach the app even while maintenance is on (and even if login/DB is broken).
function maintHeaders() {
  const b = typeof localStorage !== 'undefined' ? localStorage.getItem(MAINT_BYPASS_KEY) : null;
  return b ? { 'x-maintenance-bypass': b } : {};
}

function getStoredToken() {
  return typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
}

function setStoredToken(token) {
  if (typeof localStorage !== 'undefined' && token) localStorage.setItem(TOKEN_KEY, token);
}

function clearStoredToken() {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(TOKEN_KEY);
  resetPosthogSession();
}

// Swap the active token for an impersonation token, stashing the admin's own
// token so it can be restored on exit. Caller reloads the app afterward.
export function beginImpersonation(impersonationToken) {
  if (typeof localStorage === 'undefined') return;
  const current = localStorage.getItem(TOKEN_KEY);
  if (current && !localStorage.getItem(IMPERSONATION_BACKUP_KEY)) {
    localStorage.setItem(IMPERSONATION_BACKUP_KEY, current);
  }
  localStorage.setItem(TOKEN_KEY, impersonationToken);
  resetPosthogSession();
}

export function isImpersonating() {
  return typeof localStorage !== 'undefined' && !!localStorage.getItem(IMPERSONATION_BACKUP_KEY);
}

// Restore the admin's token. Caller reloads the app afterward.
export function endImpersonation() {
  if (typeof localStorage === 'undefined') return;
  const backup = localStorage.getItem(IMPERSONATION_BACKUP_KEY);
  if (backup) {
    localStorage.setItem(TOKEN_KEY, backup);
    localStorage.removeItem(IMPERSONATION_BACKUP_KEY);
  }
  resetPosthogSession();
}

async function getToken() {
  let token = getStoredToken();
  if (token) return token;
  const apiKey = (typeof localStorage !== 'undefined' ? localStorage.getItem(API_KEY_KEY) : null) || import.meta.env?.VITE_API_KEY;
  if (!apiKey) {
    throw new AuthError('Session expired. Please log in again.', 'SESSION_EXPIRED');
  }
  const res = await fetch(`${API_BASE}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiKey }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to get token');
  token = data.token;
  setStoredToken(token);
  return token;
}

function toQS(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) if (v != null) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
}

// Fired when any request comes back 503 MAINTENANCE mid-session, so the app
// can flip to the maintenance screen without a manual reload.
function onMaintenance(payload) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cx:maintenance', { detail: payload || {} }));
  }
}

async function req(method, path, body) {
  const token = await getToken();
  const opt = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...maintHeaders(),
    },
  };
  if (body) opt.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, opt);
  const text = await res.text().catch(() => '');
  let data;
  try { data = JSON.parse(text.trim()); } catch { data = {}; }
  if (res.status === 401) {
    clearStoredToken();
    throw new AuthError('Session expired. Please log in again.', 'SESSION_EXPIRED');
  }
  if (res.status === 503 && data?.code === 'MAINTENANCE') {
    onMaintenance(data);
    throw new AuthError(data.error || 'Under maintenance', 'MAINTENANCE');
  }
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || res.statusText || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

async function authReq(method, path, body) {
  const token = getStoredToken();
  const headers = { 'Content-Type': 'application/json', ...maintHeaders() };
  if (token) headers.Authorization = `Bearer ${token}`;
  const opt = { method, headers };
  if (body) opt.body = JSON.stringify(body);
  const res = await fetch(`/api${path}`, opt);
  const data = await res.json().catch(() => ({}));
  if (res.status === 503 && data?.code === 'MAINTENANCE') onMaintenance(data);
  return { res, data };
}

export const api = {
  setApiKey: (key) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(API_KEY_KEY, key);
  },
  getToken,
  clearToken: clearStoredToken,
  // Public, token-free — anonymous visitors can read maintenance status.
  async maintenanceStatus() {
    try {
      const res = await fetch(`${API_BASE}/maintenance`, { headers: { ...maintHeaders() } });
      if (!res.ok) return { enabled: false };
      return await res.json();
    } catch {
      return { enabled: false };
    }
  },
  logClientError: (errorMessage, errorCode, category, metadata) => {
    req('POST', '/log-client-error', { errorMessage, errorCode, category, metadata }).catch(() => {});
  },
  async login(email, password) {
    const { res, data } = await authReq('POST', '/auth/login', { email, password });
    if (!res.ok) throw new AuthError(data.error || res.statusText, data.code);
    setStoredToken(data.token);
    return { user: data.user, token: data.token };
  },
  async validateSignupToken(accessToken) {
    const { res, data } = await authReq('GET', `/auth/validate-signup-token?token=${encodeURIComponent(accessToken || '')}`);
    return res.ok ? data : { valid: false, error: data.error || 'Invalid token' };
  },
  async signup(email, password, firstName, lastName, accessToken) {
    const { res, data } = await authReq('POST', '/auth/signup', { email, password, firstName, lastName, accessToken });
    if (!res.ok) throw new AuthError(data.error || res.statusText, data.code);
    setStoredToken(data.token);
    return { user: data.user, token: data.token };
  },
  async me() {
    const { res, data } = await authReq('GET', '/auth/me');
    return res.ok ? data : null;
  },
  async updateProfile(updates) {
    const { res, data } = await authReq('PUT', '/auth/profile', updates);
    if (!res.ok) throw new AuthError(data.error || res.statusText, data.code);
    return data.user;
  },
  async uploadProfilePhoto(file) {
    const token = getStoredToken();
    if (!token) throw new AuthError('Not authenticated', 'NO_TOKEN');
    
    const formData = new FormData();
    formData.append('photo', file);
    
    const res = await fetch('/api/auth/upload-photo', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new AuthError(data.error || res.statusText, data.code);
    return data;
  },
  async forgotPassword(email) {
    const { res, data } = await authReq('POST', '/auth/forgot-password', { email });
    if (!res.ok) throw new AuthError(data.error || res.statusText, data.code);
    return data;
  },
  async resetPassword(token, password) {
    const { res, data } = await authReq('POST', '/auth/reset-password', { token, password });
    if (!res.ok) throw new AuthError(data.error || res.statusText, data.code);
    return data;
  },
  icpProfiles: {
    list: () => req('GET', '/icp-profiles'),
    create: (data) => req('POST', '/icp-profiles', data),
    update: (id, data) => req('PUT', `/icp-profiles/${id}`, data),
    getDefault: () => req('GET', '/icp-profiles/default'),
  },
  leadLists: {
    list: () => req('GET', '/lead-lists'),
    create: (data) => req('POST', '/lead-lists', data),
    import: (data) => req('POST', '/lead-lists/import', data),
    importStatus: (id) => req('GET', `/lead-lists/import-status/${id}`),
  },
  companies: {
    list: (params) => req('GET', '/companies' + (params?.list_id ? `?list_id=${params.list_id}` : '')),
    create: (data) => req('POST', '/companies', data),
  },
  leads: {
    list: (params) => {
      const q = new URLSearchParams(params || {}).toString();
      return req('GET', '/leads' + (q ? `?${q}` : ''));
    },
    stats: (listId) => req('GET', `/leads/stats?list_id=${encodeURIComponent(listId)}`),
    create: (data) => req('POST', '/leads', data),
    update: (id, data) => req('PUT', `/leads/${id}`, data),
  },
  aiSdr: {
    generateSample: (params) => req('POST', '/ai-sdr/generate-sample', params),
    activity: {
      list: (params) => { const q = new URLSearchParams(params || {}).toString(); return req('GET', '/ai-sdr/activity' + (q ? `?${q}` : '')); },
      create: (data) => req('POST', '/ai-sdr/activity', data),
    },
  },
  aiCouncil: {
    chat: (params) => req('POST', '/ai-council/chat', params),
    getSystemPrompt: () => req('GET', '/ai-council/system-prompt'),
    saveSystemPrompt: (systemPrompt) => req('POST', '/ai-council/system-prompt', { systemPrompt }),
    models: () => req('GET', '/ai-council/models'),
    council: (message) => req('POST', '/ai-council/council', { message }),
    councilStatus: (id) => req('GET', `/ai-council/council/${id}`),
    sessions: () => req('GET', '/ai-council/sessions'),
  },
  crm: {
    pipeline: (params) => {
      const q = new URLSearchParams();
      if (params?.projectId) q.set('project_id', params.projectId);
      if (params?.limit) q.set('limit', params.limit);
      if (params?.offset) q.set('offset', params.offset);
      if (params?.stage) q.set('stage', params.stage);
      if (params?.campaignReplies !== false) q.set('campaign_replies', 'true');
      return req('GET', '/crm/pipeline' + (q.toString() ? `?${q}` : ''));
    },
    create: (data) => req('POST', '/crm/leads', data),
    updateLead: (id, data) => req('PATCH', `/crm/leads/${id}`, data),
  },
  prompts: {
    list: () => req('GET', '/prompts'),
    create: (data) => req('POST', '/prompts', data),
    update: (id, data) => req('PUT', `/prompts/${id}`, data),
    delete: (id) => req('DELETE', `/prompts/${id}`),
    getDefaults: () => req('GET', '/prompts/defaults'),
    updateDefault: (key, data) => req('PUT', `/prompts/defaults/${key}`, data),
    deleteDefault: (key) => req('DELETE', `/prompts/defaults/${key}`),
    getActive: () => req('GET', '/prompts/active'),
    setActive: (key) => req('PUT', '/prompts/active', { key }),
  },
  leadGeneration: {
    getDiscoverStatus: () => req('GET', '/lead-generation/discover/status'),
    discover: (params) => req('POST', '/lead-generation/discover', params),
    discoverStart: (params) => req('POST', '/lead-generation/discover/start', params),
    discoverStatus: (jobId) => req('GET', `/lead-generation/discover/status/${jobId}`),
    discoverStream: async (params, onEvent) => {
      console.log('[Discovery API] POST /lead-generation/discover/stream');
      const token = await getToken();
      const res = await fetch(`${API_BASE}/lead-generation/discover/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const bodyText = await res.text();
        const isHtml = bodyText.trim().toLowerCase().startsWith('<!doctype html');
        const message = isHtml
          ? 'Request timed out (took longer than 30 seconds). Try fewer leads or narrower criteria.'
          : (bodyText || res.statusText);
        console.error('[Discovery API] Fetch failed:', res.status, isHtml ? 'HTML error page' : bodyText);
        throw new Error(message);
      }
      console.log('[Discovery API] Stream opened, status=', res.status);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = 'message';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('[Discovery API] Stream ended (reader done)');
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('[Discovery API] Parsed event:', currentEvent, data?.type ?? '');
                onEvent({ event: currentEvent, data });
              } catch (_) {}
            }
          }
        }
      } catch (err) {
        console.error('[Discovery API] Stream read error:', err);
        throw err;
      }
      if (buffer.trim()) {
        const remaining = buffer.split('\n');
        for (const line of remaining) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('[Discovery API] Parsed event (remaining):', currentEvent, data?.type ?? '');
              onEvent({ event: currentEvent, data });
            } catch (_) {}
          }
        }
      }
    },
    discoverApollo: (params) => req('POST', '/lead-generation/discover/apollo', params),
    discoverGoogleMaps: (params) => req('POST', '/lead-generation/discover/google-maps', params),
    discoverIcyPeas: (params) => req('POST', '/lead-generation/discover/icypeas', params),
    
    // Enrichment
    enrichEmail: (person) => req('POST', '/lead-generation/enrich/email', person),
    verifyEmail: (email) => req('POST', '/lead-generation/verify/email', { email }),
    enrichBulk: (params) => req('POST', '/lead-generation/enrich/bulk', params),
    enrichBulkStart: (params) => req('POST', '/lead-generation/enrich/bulk/start', params),
    enrichBulkStatus: (jobId) => req('GET', `/lead-generation/enrich/bulk/status/${jobId}`),
    enrichImportStart: (params) => req('POST', '/lead-generation/enrich/import/start', params),
    enrichImportStatus: (jobId) => req('GET', `/lead-generation/enrich/import/status/${jobId}`),
    enrichBulkStream: async (params, onEvent) => {
      const companies = params?.companies ?? [];
      console.log('[Enrichment API] POST /lead-generation/enrich/bulk/stream', {
        companyCount: companies.length,
        roles: params?.roles,
        listName: params?.listName,
        batchSize: params?.batchSize,
        companyIds: companies.map(c => c.id ?? c.company_id ?? '?'),
        companiesFull: companies.map(c => ({ id: c.id, name: c.name, domain: c.domain, website: c.website })),
        payload: params,
      });
      const token = await getToken();
      const res = await fetch(`${API_BASE}/lead-generation/enrich/bulk/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const bodyText = await res.text();
        const contentType = res.headers.get('Content-Type') || '';
        if (res.status === 503 && contentType.includes('text/html')) {
          console.error('[Enrichment] Request failed: 503 with Heroku error page');
          throw new Error('Server temporarily unavailable. The request may have timed out. Please try again with fewer companies or in a moment.');
        }
        let data = {};
        try {
          data = bodyText ? JSON.parse(bodyText) : {};
        } catch (_) {}
        const payloadSummary = {
          companies: params?.companies?.length ?? 0,
          roles: params?.roles ?? [],
          listName: params?.listName ?? null,
          size: JSON.stringify(params ?? {}).length,
        };
        console.error('[Enrichment] Request failed:', {
          method: 'POST',
          url: `${API_BASE}/lead-generation/enrich/bulk/stream`,
          payload: payloadSummary,
          response: {
            status: res.status,
            statusText: res.statusText,
            contentType,
            body: bodyText?.slice?.(0, 2000) ?? bodyText,
          },
        });
        throw new Error(data.error || res.statusText);
      }
      console.log('[Enrichment API] Stream opened, status=', res.status);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = 'message';
      let eventCount = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: !done });
        if (done) {
          console.log('[Enrichment API] Stream ended (reader done), events processed:', eventCount);
          if (eventCount < 5) {
            console.warn('[Enrichment API] Few events received (' + eventCount + ') — possible early termination or proxy buffering. Check Heroku logs for backend activity.');
          }
          break;
        }
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              eventCount++;
              console.log('[Enrichment API] SSE event:', currentEvent, data);
              if (currentEvent === 'done' || currentEvent === 'error') {
                console.log('[Enrichment API] Parsed event (final):', currentEvent, data);
              }
              onEvent({ event: currentEvent, data });
            } catch (_) {}
            currentEvent = 'message';
          }
        }
      }
      if (buffer.trim()) {
        const remaining = buffer.split('\n');
        for (const line of remaining) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              eventCount++;
              console.log('[Enrichment API] SSE event (remaining):', currentEvent, data);
              if (currentEvent === 'done' || currentEvent === 'error') {
                console.log('[Enrichment API] Parsed event (final):', currentEvent, data);
              }
              onEvent({ event: currentEvent, data });
            } catch (_) {}
          }
        }
      }
      console.log('[Enrichment API] enrichBulkStream returned (stream consumed)');
    },
    scrapeWebsite: (url) => req('POST', '/lead-generation/scrape/website', { url }),
    getLinkedInCompany: (slug) => req('GET', `/lead-generation/linkedin/company/${slug}`),
    
    // Personalization
    personalize: (params) => req('POST', '/lead-generation/personalize', params),
    
    // Outreach
    sendToHeyReach: (leads) => req('POST', '/lead-generation/outreach/heyreach', { leads }),
    sendToInstantly: (leads) => req('POST', '/lead-generation/outreach/instantly', { leads }),

    // Leads Management — AI Ark single-pass stream
    leadsManagementStream: async (params, onEvent) => {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/lead-generation/leads-management/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.startsWith('<!') ? 'Request timed out' : (JSON.parse(text).error || res.statusText));
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '', currentEvent = 'message';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
          else if (line.startsWith('data: ')) {
            try { onEvent({ event: currentEvent, data: JSON.parse(line.slice(6)) }); } catch (_) {}
            currentEvent = 'message';
          }
        }
      }
    },
  },
  enrichmentV2: {
    start: (filters, requestedSize, instantlyCampaignId, promptText) => {
      const body = { filters, requestedSize };
      if (instantlyCampaignId != null && String(instantlyCampaignId).trim() !== '') {
        body.instantly_campaign_id = String(instantlyCampaignId).trim();
      }
      if (promptText != null && String(promptText).trim() !== '') {
        body.promptText = String(promptText).trim();
      }
      return req('POST', '/enrichment-v2/start', body);
    },
    status: (jobId) => req('GET', `/enrichment-v2/status/${jobId}`),
    stop: (jobId) => req('POST', `/enrichment-v2/stop/${jobId}`),
    jobs: (status) => req('GET', `/enrichment-v2/jobs${status ? `?status=${status}` : ''}`),
    jobLeadIds: (jobId) => req('GET', `/enrichment-v2/jobs/${encodeURIComponent(jobId)}/lead-ids`),
    syncLog: (jobId, after = 0) =>
      req('GET', `/enrichment-v2/jobs/${encodeURIComponent(jobId)}/sync-log?after=${encodeURIComponent(after)}`),
    jobLeadsDetail: (jobId) => req('GET', `/enrichment-v2/jobs/${encodeURIComponent(jobId)}/leads-detail`),
    preview: (filters) => req('POST', '/enrichment-v2/preview', { filters }),
  },
  settings: {
    get: (type, params) => {
      const q = new URLSearchParams();
      if (params?.projectId) q.set('project_id', params.projectId);
      return req('GET', `/settings/${type}` + (q.toString() ? `?${q}` : ''));
    },
    save: (type, settings, params) => {
      const body = { settings };
      if (params?.projectId != null) body.projectId = params.projectId;
      return req('POST', `/settings/${type}` + (params?.projectId != null ? `?project_id=${encodeURIComponent(params.projectId)}` : ''), body);
    },
    getFormSchema: (formType) => req('GET', `/settings/schema/${formType}`),
  },
  integrations: {
    list: () => req('GET', '/integrations'),
    get: (key) => req('GET', `/integrations/${encodeURIComponent(key)}`),
    save: (key, credentials) => req('POST', `/integrations/${encodeURIComponent(key)}`, { credentials }),
    connect: (path, credentials) => req('POST', `/integrations${path}`, { credentials }),
    check: (key) => req('POST', `/integrations/${encodeURIComponent(key)}/check`),
    getNylasAuthUrl: () => req('GET', '/integrations/nylas/auth'),
    getCosts: () => req('GET', '/integrations/costs'),
    getLeadSearchOrder: () => req('GET', '/integrations/order/lead-search'),
    saveLeadSearchOrder: (order) => req('POST', '/integrations/order/lead-search', order),
  },
  instantly: {
    campaigns: {
      list: (opts) => req('GET', '/instantly/campaigns' + toQS(opts)),
      get: (id) => req('GET', `/instantly/campaigns/${id}`),
      create: (data) => req('POST', '/instantly/campaigns', data),
      activate: (id) => req('POST', `/instantly/campaigns/${id}/activate`),
      pause: (id) => req('POST', `/instantly/campaigns/${id}/pause`),
    },
    analytics: (opts) => req('GET', '/instantly/analytics' + toQS(opts)),
    analyticsOverview: (opts) => req('GET', '/instantly/analytics/overview' + toQS(opts)),
    leads: {
      create: (data) => req('POST', '/instantly/leads', data),
      bulk: (leads, campaignId) => req('POST', '/instantly/leads/bulk', { leads, campaign_id: campaignId }),
      get: (id) => req('GET', `/instantly/leads/${id}`),
      list: (opts) => req('POST', '/instantly/leads/list', opts || {}),
    },
    accounts: (opts) => req('GET', '/instantly/accounts' + toQS(opts)),
    emails: {
      list: (opts) => req('GET', '/instantly/emails' + toQS(opts)),
      unreadCount: () => req('GET', '/instantly/emails/unread/count'),
    },
    leadLists: {
      list: (opts) => req('GET', '/instantly/lead-lists' + toQS(opts)),
      create: (name) => req('POST', '/instantly/lead-lists', { name }),
    },
  },
  smartlead: {
    campaigns: {
      list: (opts) => req('GET', '/smartlead/campaigns' + toQS(opts)),
    },
    leads: {
      add: (campaignId, leadList) => req('POST', `/smartlead/campaigns/${encodeURIComponent(campaignId)}/leads`, { lead_list: leadList }),
    },
  },
  heyreach: {
    campaigns: {
      list: (opts) => req('GET', '/heyreach/campaigns' + toQS(opts)),
      get: (id) => req('GET', `/heyreach/campaigns/${id}`),
      pause: (id) => req('POST', `/heyreach/campaigns/${id}/pause`),
      resume: (id) => req('POST', `/heyreach/campaigns/${id}/resume`),
      addLeads: (id, leads) => req('POST', `/heyreach/campaigns/${id}/leads`, { leads }),
      addLeadsDefault: (leads, campaignId) => req('POST', '/heyreach/campaigns/add-leads', { leads, campaignId }),
    },
    leads: {
      lookup: (profileUrl) => req('POST', '/heyreach/leads/lookup', { profileUrl }),
    },
    conversations: (opts) => req('POST', '/heyreach/conversations', opts || {}),
    stats: (opts) => req('POST', '/heyreach/stats', opts || {}),
    lists: {
      list: (opts) => req('GET', '/heyreach/lists' + toQS(opts)),
      create: (name, listType) => req('POST', '/heyreach/lists', { name, listType }),
    },
    network: (senderId) => req('POST', '/heyreach/network', { senderId }),
  },
  aimfox: {
    accounts: () => req('GET', '/aimfox/accounts'),
    campaigns: {
      list: (opts) => req('GET', '/aimfox/campaigns' + toQS(opts)),
      create: (data) => req('POST', '/aimfox/campaigns', data),
      get: (id) => req('GET', `/aimfox/campaigns/${id}`),
      update: (id, data) => req('PATCH', `/aimfox/campaigns/${id}`, data),
      metrics: (id) => req('GET', `/aimfox/campaigns/${id}/metrics`),
      addProfile: (id, profileUrl) => req('POST', `/aimfox/campaigns/${id}/audience`, { profile_url: profileUrl }),
      addProfiles: (id, profiles) => req('POST', `/aimfox/campaigns/${id}/audience/multiple`, { profiles }),
    },
    conversations: (opts) => req('GET', '/aimfox/conversations' + toQS(opts)),
    getMessages: (accountId, conversationUrn) => req('GET', `/aimfox/accounts/${accountId}/conversations/${encodeURIComponent(conversationUrn)}`),
    startConversation: (accountId, data) => req('POST', `/aimfox/accounts/${accountId}/conversations`, data),
    reply: (accountId, conversationUrn, message) => req('POST', `/aimfox/accounts/${accountId}/conversations/${encodeURIComponent(conversationUrn)}`, { message }),
    markAsRead: (accountId, conversationUrn) => req('POST', `/aimfox/accounts/${accountId}/conversations/${encodeURIComponent(conversationUrn)}/mark-as-read`),
    leads: {
      get: (id) => req('GET', `/aimfox/leads/${id}`),
      search: (filters, opts) => req('POST', '/aimfox/leads/search' + toQS(opts), filters),
    },
    analytics: (opts) => req('GET', '/aimfox/analytics/interactions' + toQS(opts)),
  },
  calendar: {
    getEvents: (date) => req('GET', '/calendar/events' + (date ? `?date=${encodeURIComponent(date)}` : '')),
    getRange: (start, end) => req('GET', `/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
    createEvent: (data) => req('POST', '/calendar/events', data),
  },
  stats: {
    dashboard: (params) => {
      const q = new URLSearchParams();
      if (params?.projectId) q.set('project_id', params.projectId);
      return req('GET', '/stats/dashboard' + (q.toString() ? `?${q}` : ''));
    },
    chart: (params) => {
      const q = new URLSearchParams();
      if (params?.projectId && !params?.orgWide) q.set('project_id', params.projectId);
      if (params?.range) q.set('range', params.range);
      if (params?.today) q.set('today', params.today);
      return req('GET', '/stats/chart' + (q.toString() ? `?${q}` : ''));
    },
  },
  organisations: {
    list: () => req('GET', '/organisations'),
    create: (data) => req('POST', '/organisations', data),
  },
  conversations: {
    list: () => req('GET', '/conversations'),
    get: (id) => req('GET', `/conversations/${id}`),
    create: (data) => req('POST', '/conversations', data),
    update: (id, data) => req('PUT', `/conversations/${id}`, data),
    delete: (id) => req('DELETE', `/conversations/${id}`),
    sync: (opts) => req('POST', '/unibox/sync', opts || {}),
    status: () => req('GET', '/unibox/status'),
    fixLinks: () => req('POST', '/unibox/fix-links'),
    thread: (conversationId) => req('GET', `/unibox/thread/${conversationId}`),
  },
  messages: {
    list: (conversationId) => req('GET', `/messages?conversation_id=${conversationId}`),
    create: (data) => req('POST', '/messages', data),
  },
  activity: {
    list: (params) => {
      const q = new URLSearchParams(params || {}).toString();
      return req('GET', '/activity' + (q ? `?${q}` : ''));
    },
    create: (data) => req('POST', '/activity', data),
  },
  audit: {
    research: (data) => req('POST', '/audit/research', data),
    projects: {
      list: () => req('GET', '/audit/projects'),
      create: (data) => req('POST', '/audit/projects', data),
    },
    surveys: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/surveys' + (q ? `?${q}` : ''));
      },
      get: (id) => req('GET', `/audit/surveys/${id}`),
      create: (data) => req('POST', '/audit/surveys', data),
      update: (id, data) => req('PUT', `/audit/surveys/${id}`, data),
      delete: (id) => req('DELETE', `/audit/surveys/${id}`),
      listResponses: (id) => req('GET', `/audit/surveys/${id}/responses`),
      submitResponse: (id, data) => req('POST', `/audit/surveys/${id}/responses`, data),
    },
    interviews: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/interviews' + (q ? `?${q}` : ''));
      },
      create: (data) => req('POST', '/audit/interviews', data),
      generate: (data) => req('POST', '/audit/interviews/generate', data),
      update: (id, data) => req('PUT', `/audit/interviews/${id}`, data),
      delete: (id) => req('DELETE', `/audit/interviews/${id}`),
    },
    transcripts: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/transcripts' + (q ? `?${q}` : ''));
      },
      create: (data) => req('POST', '/audit/transcripts', data),
      update: (id, data) => req('PUT', `/audit/transcripts/${id}`, data),
      delete: (id) => req('DELETE', `/audit/transcripts/${id}`),
    },
    processMaps: {
      list: (projectId) => req('GET', '/audit/process-maps' + (projectId ? `?project_id=${encodeURIComponent(projectId)}` : '')),
      create: (data) => req('POST', '/audit/process-maps', data),
      update: (id, data) => req('PUT', `/audit/process-maps/${id}`, data),
      delete: (id) => req('DELETE', `/audit/process-maps/${id}`),
      chat: (data) => req('POST', '/audit/process-maps/chat', data),
      generate: (data) => req('POST', '/audit/process-maps/generate', data),
      exportPptx: async (pillars) => {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/audit/process-maps/export/pptx`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ pillars }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
        const blob = await res.blob();
        return { blob, filename: 'Process_Map.pptx' };
      },
      exportPdf: async (pillars) => {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/audit/process-maps/export/pdf`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ pillars }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
        const html = await res.text();
        return html;
      },
      addToDeck: (pillars) => req('POST', '/audit/process-maps/add-to-deck', { pillars }),
    },
    analyse: (data) => req('POST', '/audit/analyse', data),
    analyseChat: (data) => req('POST', '/audit/analyse/chat', data),
    analyses: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/analyses' + (q ? `?${q}` : ''));
      },
      get: (id) => req('GET', `/audit/analyses/${id}`),
    },
    decks: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/decks' + (q ? `?${q}` : ''));
      },
      latest: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/decks/latest' + (q ? `?${q}` : ''));
      },
      get: (id) => req('GET', `/audit/decks/${id}`),
      create: (data) => req('POST', '/audit/decks', data),
      update: (id, data) => req('PUT', `/audit/decks/${id}`, data),
      delete: (id) => req('DELETE', `/audit/decks/${id}`),
      generate: (data) => req('POST', '/audit/decks/generate', data),
      generateLatest: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/decks/generate/latest' + (q ? `?${q}` : ''));
      },
      generatePoll: (jobId) => req('GET', `/audit/decks/generate/${jobId}`),
      generateCancel: (jobId) => req('POST', `/audit/decks/generate/${jobId}/cancel`),
      templates: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/audit/deck-templates' + (q ? `?${q}` : ''));
      },
      exportPptx: async (id) => {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/audit/decks/${id}/export/pptx`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
        const blob = await res.blob();
        const cd = res.headers.get('content-disposition') || '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : 'deck.pptx';
        return { blob, filename };
      },
      exportHtml: (id) => `${API_BASE}/audit/decks/${id}/export/html?download=true`,
      exportPdf: (id) => `${API_BASE}/audit/decks/${id}/export/pdf`,
    },
    deckStyles: {
      list: () => req('GET', '/audit/deck-styles'),
    },
    deckPromptVariants: {
      list: () => req('GET', '/audit/deck-prompt-variants'),
    },
  },
  implementation: {
    list: (params) => {
      const q = new URLSearchParams(params || {}).toString();
      return req('GET', '/implementation/phases' + (q ? `?${q}` : ''));
    },
    create: (data) => req('POST', '/implementation/phases', data),
    bulkCreate: (data) => req('POST', '/implementation/phases/bulk', data),
    update: (id, data) => req('PUT', `/implementation/phases/${id}`, data),
    delete: (id) => req('DELETE', `/implementation/phases/${id}`),
  },
  workflows: {
    list: () => req('GET', '/workflows'),
    get: (id) => req('GET', `/workflows/${id}`),
    create: (data) => req('POST', '/workflows', data),
    update: (id, data) => req('PUT', `/workflows/${id}`, data),
    delete: (id) => req('DELETE', `/workflows/${id}`),
    seed: () => req('POST', '/workflows/seed'),
    seedLibrary: () => req('POST', '/workflows/seed-library'),
    rate: (id, rating) => req('POST', `/workflows/${id}/rate`, { rating }),
    download: (id) => req('POST', `/workflows/${id}/download`),
  },
  messagingCopies: {
    list: () => req('GET', '/messaging-copies'),
    create: (data) => req('POST', '/messaging-copies', data),
    update: (id, data) => req('PUT', `/messaging-copies/${id}`, data),
    delete: (id) => req('DELETE', `/messaging-copies/${id}`),
    generate: (data) => req('POST', '/messaging-copies/generate', data),
    copywriterChat: (data) => req('POST', '/messaging-copies/copywriter-chat', data),
  },
  campaigns: {
    list: () => req('GET', '/campaigns'),
    get: (id) => req('GET', `/campaigns/${id}`),
    create: (data) => req('POST', '/campaigns', data),
    update: (id, data) => req('PUT', `/campaigns/${id}`, data),
    delete: (id) => req('DELETE', `/campaigns/${id}`),
  },
  niches: {
    list: () => req('GET', '/niches'),
    create: (data) => req('POST', '/niches', data),
    update: (id, data) => req('PUT', `/niches/${id}`, data),
    delete: (id) => req('DELETE', `/niches/${id}`),
    chat: (params) => req('POST', '/niches/chat', params),
  },
  salesScripts: {
    list: () => req('GET', '/sales-scripts'),
    create: (data) => req('POST', '/sales-scripts', data),
    update: (id, data) => req('PUT', `/sales-scripts/${id}`, data),
    delete: (id) => req('DELETE', `/sales-scripts/${id}`),
    generate: (data) => req('POST', '/sales-scripts/generate', data),
  },
  callAnalyses: {
    list: () => req('GET', '/call-analyses'),
    get: (id) => req('GET', `/call-analyses/${id}`),
    create: (data) => req('POST', '/call-analyses', data),
    analyse: (data) => req('POST', '/call-analyses/analyse', data),
    objections: () => req('GET', '/call-analyses/objections'),
  },
  fathom: {
    status: () => req('GET', '/fathom/status'),
    meetings: (params) => {
      const q = new URLSearchParams(params || {}).toString();
      return req('GET', '/fathom/meetings' + (q ? `?${q}` : ''));
    },
    transcript: (recordingId) => req('GET', `/fathom/meetings/${recordingId}/transcript`),
    import: (recordingId, data) => req('POST', `/fathom/import/${recordingId}`, data),
  },
  contentPosts: {
    list: (params) => {
      const q = new URLSearchParams(params || {}).toString();
      return req('GET', '/content-posts' + (q ? `?${q}` : ''));
    },
    create: (data) => req('POST', '/content-posts', data),
    update: (id, data) => req('PUT', `/content-posts/${id}`, data),
    delete: (id) => req('DELETE', `/content-posts/${id}`),
    extractInsights: () => req('POST', '/content-posts/extract-insights', {}),
    generate: (data) => req('POST', '/content-posts/generate', data),
    generateVideo: (data) => req('POST', '/content-posts/generate-video', data),
  },
  trackedCompetitors: {
    list: () => req('GET', '/tracked-competitors'),
    create: (data) => req('POST', '/tracked-competitors', data),
    delete: (id) => req('DELETE', `/tracked-competitors/${id}`),
  },
  community: {
    accounts: {
      list: () => req('GET', '/community/accounts'),
      create: (data) => req('POST', '/community/accounts', data),
      delete: (id) => req('DELETE', `/community/accounts/${id}`),
    },
    keywords: {
      list: () => req('GET', '/community/keywords'),
      create: (data) => req('POST', '/community/keywords', data),
      delete: (id) => req('DELETE', `/community/keywords/${id}`),
    },
    voiceSamples: {
      list: () => req('GET', '/community/voice-samples'),
      create: (data) => req('POST', '/community/voice-samples', data),
      delete: (id) => req('DELETE', `/community/voice-samples/${id}`),
    },
    feed: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/community/feed' + (q ? `?${q}` : ''));
      },
      updateReply: (id, data) => req('PUT', `/community/feed/${id}/reply`, data),
      updateStatus: (id, data) => req('PUT', `/community/feed/${id}/status`, data),
    },
  },
  assistant: {
    chat: (data) => req('POST', '/assistant/chat', data),
    history: () => req('GET', '/assistant/history'),
    clearHistory: () => req('DELETE', '/assistant/history'),
  },
  billing: {
    plan: () => req('GET', '/billing/plan'),
    invoices: () => req('GET', '/billing/invoices'),
    credits: () => req('GET', '/billing/credits'),
    usageByAction: () => req('GET', '/billing/credits/usage-by-action'),
    creditCosts: () => req('GET', '/billing/credits/costs'),
    plans: () => req('GET', '/billing/plans'),
    creditHistory: () => req('GET', '/billing/credits/history'),
    checkout: (data) => req('POST', '/billing/checkout', data),
    creditPackInfo: () => req('GET', '/billing/credit-pack-info'),
    subscribe: (data) => req('POST', '/billing/subscribe', data),
    portal: () => req('POST', '/billing/portal'),
  },
  notifications: {
    list: (params) => {
      const q = new URLSearchParams(params || {}).toString();
      return req('GET', '/notifications' + (q ? `?${q}` : ''));
    },
    markRead: (id) => req('PUT', `/notifications/${id}/read`),
    markAllRead: () => req('PUT', '/notifications/read-all'),
  },
  websites: {
    list: () => req('GET', '/websites'),
    create: (data) => req('POST', '/websites', data),
    update: (id, data) => req('PUT', `/websites/${id}`, data),
    del: (id) => req('DELETE', `/websites/${id}`),
    publish: (id) => req('POST', `/websites/${id}/publish`),
  },
  sendingAccounts: {
    list: (type) => req('GET', `/sending-accounts${type ? '?type=' + type : ''}`),
    create: (data) => req('POST', '/sending-accounts', data),
    update: (id, data) => req('PUT', `/sending-accounts/${id}`, data),
    del: (id) => req('DELETE', `/sending-accounts/${id}`),
  },
  prototypes: {
    list: (projectId) => req('GET', `/prototypes${projectId ? '?project_id=' + projectId : ''}`),
    generate: (data) => req('POST', '/prototypes/generate', data),
  },
  booking: {
    eventTypes: {
      list: () => req('GET', '/booking/event-types'),
      create: (data) => req('POST', '/booking/event-types', data),
      update: (id, data) => req('PUT', `/booking/event-types/${id}`, data),
      del: (id) => req('DELETE', `/booking/event-types/${id}`),
      sync: () => req('POST', '/booking/event-types/sync'),
    },
    availability: {
      get: () => req('GET', '/booking/availability'),
      update: (data) => req('PUT', '/booking/availability', data),
      sync: () => req('POST', '/booking/availability/sync'),
    },
  },
  admin: {
    billingPlans: {
      list: () => req('GET', '/admin/billing-plans'),
      update: (id, data) => req('PUT', `/admin/billing-plans/${id}`, data),
    },
    agencies: {
      list: () => req('GET', '/admin/agencies'),
      create: (data) => req('POST', '/admin/agencies', data),
      update: (id, data) => req('PUT', `/admin/agencies/${id}`, data),
      delete: (id) => req('DELETE', `/admin/agencies/${id}`),
    },
    organisations: {
      list: () => req('GET', '/admin/organisations'),
      get: (id) => req('GET', `/admin/organisations/${id}`),
      adjustCredits: (id, data) => req('PUT', `/admin/organisations/${id}/credits`, data),
      changePlan: (id, data) => req('PUT', `/admin/organisations/${id}/plan`, data),
      updateTrial: (id, data) => req('PUT', `/admin/organisations/${id}/trial`, data),
      getDecks: (id) => req('GET', `/admin/organisations/${id}/decks`),
      impersonate: (id, userId) => req('POST', `/admin/organisations/${id}/impersonate`, userId ? { userId } : {}),
      getIntegrations: (orgId) => req('GET', `/admin/organisations/${encodeURIComponent(orgId)}/integrations`),
      saveIntegration: (orgId, key, credentials) => req('POST', `/admin/organisations/${encodeURIComponent(orgId)}/integrations/${encodeURIComponent(key)}`, { credentials }),
      getIntegration: (orgId, key) => req('GET', `/admin/organisations/${encodeURIComponent(orgId)}/integrations/${encodeURIComponent(key)}`),
      getLeadSearchOrder: (orgId) => req('GET', `/admin/organisations/${encodeURIComponent(orgId)}/integrations/order/lead-search`),
      saveLeadSearchOrder: (orgId, order) => req('POST', `/admin/organisations/${encodeURIComponent(orgId)}/integrations/order/lead-search`, order),
    },
    maintenance: {
      get: () => req('GET', '/admin/maintenance'),
      set: (payload) => req('POST', '/admin/maintenance', payload),
    },
    auditDecks: {
      get: (id) => req('GET', `/admin/audit-decks/${id}`),
    },
    users: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/admin/users' + (q ? `?${q}` : ''));
      },
      updateStatus: (id, is_suspended) => req('PUT', `/admin/users/${id}/suspend`, { is_suspended }),
      resetPassword: (id) => req('PUT', `/admin/users/${id}/reset-password`),
    },
    signupAccessToken: {
      get: () => req('GET', '/admin/signup-access-token'),
      update: (data) => req('PUT', '/admin/signup-access-token', data),
    },
    supportTickets: {
      list: (params) => {
        const q = new URLSearchParams(params || {}).toString();
        return req('GET', '/admin/support-tickets' + (q ? `?${q}` : ''));
      },
      create: (data) => req('POST', '/admin/support-tickets', data),
      update: (id, data) => req('PUT', `/admin/support-tickets/${id}`, data),
    },
    featureFlags: {
      list: () => req('GET', '/admin/feature-flags'),
      update: (id, data) => req('PUT', `/admin/feature-flags/${id}`, data),
    },
    systemHealth: () => req('GET', '/admin/system-health'),
    errors: {
      list: () => req('GET', '/admin/errors'),
      resolve: (id, data) => req('PUT', `/admin/errors/${id}/resolve`, data),
    },
    creditCosts: {
      list: () => req('GET', '/admin/credit-costs'),
      update: (actionKey, data) => req('PUT', `/admin/credit-costs/${encodeURIComponent(actionKey)}`, data),
    },
    systemPrompts: {
      list: () => req('GET', '/admin/system-prompts'),
      get: (id) => req('GET', `/admin/system-prompts/${id}`),
      create: (data) => req('POST', '/admin/system-prompts', data),
      update: (id, data) => req('PUT', `/admin/system-prompts/${id}`, data),
      delete: (id) => req('DELETE', `/admin/system-prompts/${id}`),
      seed: () => req('POST', '/admin/system-prompts/seed'),
      sync: () => req('POST', '/admin/system-prompts/sync'),
      modules: () => req('GET', '/admin/system-prompts/modules'),
    },
    deckPromptFiles: {
      list: () => req('GET', '/admin/deck-prompt-files'),
      update: (id, data) => req('PUT', `/admin/deck-prompt-files/${id}`, data),
      setDefault: (id) => req('POST', `/admin/deck-prompt-files/${id}/set-default`),
    },
    enrichmentFallback: {
      get: () => req('GET', '/admin/enrichment-fallback'),
      update: (data) => req('PUT', '/admin/enrichment-fallback', data),
      resetEvents: () => req('DELETE', '/admin/enrichment-fallback/events'),
    },
  },
};
